/**
 * Utility functions for formatting numbers, dates, and currencies
 * All amounts formatted in Indian number system (₹ with lakhs/crores)
 */

export const formatIndianCurrency = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '₹0';
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 10000000) {
    return `${sign}₹${(absNum / 10000000).toFixed(2)} Cr`;
  } else if (absNum >= 100000) {
    return `${sign}₹${(absNum / 100000).toFixed(2)} L`;
  } else if (absNum >= 1000) {
    return `${sign}₹${(absNum / 1000).toFixed(1)}K`;
  }
  return `${sign}₹${absNum.toLocaleString('en-IN')}`;
};

export const formatIndianNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Number(num).toLocaleString('en-IN');
};

export const formatFullIndianCurrency = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '₹0';
  return `₹${Number(num).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export const formatPercent = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0%';
  return `${Math.round(Number(num))}%`;
};

export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(date.getDate()).padStart(2, '0')} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const str = String(dateStr).trim();

  // Try DD/MM/YYYY or DD-MM-YYYY first (common in Indian data)
  const parts = str.split(/[/-]/);
  if (parts.length === 3) {
    // If first part > 12, it's definitely DD/MM/YYYY
    if (Number(parts[0]) > 12) {
      const d = new Date(parts[2], parts[1] - 1, parts[0]);
      if (!isNaN(d.getTime())) return d;
    }
    // If first part is 4 digits, it's YYYY-MM-DD
    if (parts[0].length === 4) {
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      if (!isNaN(d.getTime())) return d;
    }
    // If second part > 12, it's MM/DD/YYYY — fallback
    if (Number(parts[1]) > 12) {
      const d = new Date(parts[2], parts[0] - 1, parts[1]);
      if (!isNaN(d.getTime())) return d;
    }
    // Ambiguous (e.g. 10/06/2025) — assume DD/MM/YYYY (Indian format)
    if (parts[0].length <= 2 && parts[2].length === 4) {
      const d = new Date(parts[2], parts[1] - 1, parts[0]);
      if (!isNaN(d.getTime())) return d;
    }
  }

  // Fallback to native parsing (handles ISO, "Jun 10 2025", etc.)
  const date = new Date(str);
  if (!isNaN(date.getTime())) return date;

  return null;
};

export const getAgeGroup = (age) => {
  if (age === null || age === undefined) return 'Unknown';
  const a = Number(age);
  if (a <= 10) return '0-10';
  if (a <= 20) return '10-20';
  if (a <= 30) return '20-30';
  if (a <= 40) return '30-40';
  if (a <= 50) return '40-50';
  return '50+';
};

export const getClaimAmountBand = (amount) => {
  if (amount === null || amount === undefined) return 'Unknown';
  const a = Number(amount);
  if (a <= 10000) return '0-10K';
  if (a <= 20000) return '10-20K';
  if (a <= 30000) return '20-30K';
  if (a <= 40000) return '30-40K';
  if (a <= 50000) return '40-50K';
  if (a <= 100000) return '50K-1L';
  return '1L+';
};

export const getTATBucket = (tat) => {
  if (tat === null || tat === undefined) return 'Unknown';
  const t = Number(tat);
  if (t <= 7) return '0-7 Days';
  if (t <= 14) return '7-14 Days';
  if (t <= 21) return '15-21 Days';
  if (t <= 28) return '22-28 Days';
  return '28+ Days';
};

export const COLORS = [
  '#7C3AED', '#A78BFA', '#C4B5FD', '#8B5CF6', '#6D28D9',
  '#5B21B6', '#4C1D95', '#DDD6FE', '#EDE9FE', '#F5F3FF',
  '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#EC4899',
  '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#14B8A6'
];

export const CHART_COLORS = {
  primary: '#7C3AED',
  secondary: '#A78BFA',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#3B82F6',
  purple: '#8B5CF6',
  pink: '#EC4899',
};

export const STATUS_COLORS = {
  Paid: '#10B981',
  Outstanding: '#F59E0B',
  Rejected: '#EF4444',
  Settled: '#10B981',
  Approved: '#10B981',
};
